import type { HealthcareService } from '@medplum/fhirtypes';

import { Grid, Stack, Tag, Text } from '@eluve/components';

import {
  CodeSelectInput,
  IntegerInput,
  ReferenceMultiSelectInput,
  StringInput,
} from '../../components/inputs';
import { useFhirEditState } from '../../core/FhirEditScope';
import { useFhirReferenceOptions } from '../../core/reference-options';
import type { FhirView } from '../../core/view-contract';

import {
  APPOINTMENT_TYPE_SYSTEM,
  type SchedulingParameters,
  schedulingParameters,
  withSchedulingParameters,
} from './scheduling';

// Radix Select items can't carry an empty-string value.
const NO_CLASSIFICATION = 'none';

const CLASSIFICATION_OPTIONS = [
  {
    value: NO_CLASSIFICATION,
    label: 'None',
    description: 'No visit type — booked the same for every patient.',
  },
  {
    value: 'new-patient',
    label: 'First visit',
    description: "A patient's first appointment for this service.",
  },
  {
    value: 'returning',
    label: 'Follow-up',
    description: 'A return visit for an existing patient.',
  },
] as const;

function withClassification(
  data: HealthcareService,
  code: string,
): HealthcareService {
  if (code === NO_CLASSIFICATION) return { ...data, type: undefined };
  return {
    ...data,
    type: [{ coding: [{ system: APPOINTMENT_TYPE_SYSTEM, code }] }],
  };
}

export const HealthcareServiceEditView: FhirView<HealthcareService> = ({
  data,
}) => {
  const { current, setCurrent, dirty } = useFhirEditState(data);
  const parameters = schedulingParameters(current);
  // Undefined unless the feature lib supplied a roster — no roster, no picker.
  const locationOptions = useFhirReferenceOptions('Location');

  const setParameter = (
    name: keyof SchedulingParameters,
    value: number | undefined,
  ) =>
    setCurrent(
      withSchedulingParameters(current, { ...parameters, [name]: value }),
    );

  return (
    // Chrome rule: wears the card chrome of the read variant it replaces.
    <Stack
      gap={3}
      data-view="healthcare-service-edit"
      className="rounded-lg border border-border-primary bg-bg-primary p-4"
    >
      <Stack direction="horizontal" gap={2} align="center" justify="between">
        <Text inline size="sm" weight="semibold">
          Service
        </Text>
        {dirty && <Tag pill subtle size="xs" color="warning" label="Edited" />}
      </Stack>
      {/* Name, Classification and Description take their own full-width rows —
          the classification dropdown and description text need the room. The
          numeric scheduling fields stay paired in a two-column grid. */}
      <Stack gap={3} align="stretch">
        <StringInput
          label="Name"
          value={current.name}
          placeholder="e.g. Initial Consultation"
          hint="The service's internal name. Patients see the description, not this."
          onChange={(next) => setCurrent({ ...current, name: next })}
        />
        <CodeSelectInput
          label="Classification"
          hint="Marks this service as a first visit or a follow-up, so scheduling and reporting can tell them apart. Optional."
          value={
            current.type
              ?.flatMap((concept) => concept.coding ?? [])
              .find((coding) => coding.system === APPOINTMENT_TYPE_SYSTEM)
              ?.code ?? NO_CLASSIFICATION
          }
          options={CLASSIFICATION_OPTIONS}
          onChange={(next) => setCurrent(withClassification(current, next))}
        />
        <StringInput
          label="Description"
          value={current.comment}
          placeholder="What patients see when booking"
          hint="Patient-facing text shown at booking time."
          onChange={(next) => setCurrent({ ...current, comment: next })}
        />
      </Stack>
      <Grid columns={1} className="gap-x-6 gap-y-3 sm:grid-cols-2">
        <IntegerInput
          label="Duration (minutes)"
          value={parameters.durationMinutes}
          hint="How long the appointment lasts. Determines how much of the calendar the booking occupies."
          onChange={(next) => setParameter('durationMinutes', next)}
        />
        <IntegerInput
          label="Slot spacing (minutes)"
          value={parameters.alignmentIntervalMinutes}
          hint="How far apart offered start times are, independent of how long the visit runs. A 60-minute visit with 30-minute spacing can start at 9:00, 9:30, 10:00 — so bookings can overlap or stagger. Blank falls back to the duration (back-to-back slots)."
          onChange={(next) => setParameter('alignmentIntervalMinutes', next)}
        />
        <IntegerInput
          label="Buffer before (minutes)"
          value={parameters.bufferBeforeMinutes}
          hint="Blocked prep time held before each appointment. Not shown to patients and not part of the visit length."
          onChange={(next) => setParameter('bufferBeforeMinutes', next)}
        />
        <IntegerInput
          label="Buffer after (minutes)"
          value={parameters.bufferAfterMinutes}
          hint="Blocked cleanup/turnaround time held after each appointment, before the next booking can start."
          onChange={(next) => setParameter('bufferAfterMinutes', next)}
        />
      </Grid>
      {locationOptions && (
        <ReferenceMultiSelectInput
          label="Locations"
          hint="The clinic locations that offer this service."
          value={current.location}
          onChange={(next) => setCurrent({ ...current, location: next })}
          options={locationOptions}
          emptyLabel="No locations available"
        />
      )}
    </Stack>
  );
};
